/**
 * @ComponentFor CssAndScriptBlock
 */
import React, { useEffect } from 'react';
import connect from 'Shared/connect';
import { epiPropertyValue } from '@polarnopyret/scope-episerver';
import CssAndScriptBlockType from './CssAndScriptBlock.type';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';

type ConnectedPropType = {
  currentBreakpoint: number;
};

type PropType = CssAndScriptBlockType & ConnectedPropType;

const CssAndScriptBlock = (props: PropType) => {
  const addStyleElementToDocument = (elementId: string, value: string) => {
    if (!value || value === "") {
      return;
    }
    const htmlElement = document.createElement("style");
    htmlElement.id = elementId;
    htmlElement.innerHTML = value;
    document.body.appendChild(htmlElement);
  }

  const addScriptElementToDocument = (elementId: string, value: string) => {
    if (!value || value === "") {
      return;
    }

    const htmlElement = document.createElement("script");
    htmlElement.id = elementId;
    htmlElement.innerHTML = value;
    htmlElement.type = "text/javascript";
    document.body.appendChild(htmlElement);
  }

  const removeElementFromDocument = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.parentElement.removeChild(element);
    }
  }

  const addScriptSourceToDocumentHead = (elementId: string, values: string[]) => {
    if (!values || values.length < 1) {
      return;
    }

    values.forEach((value, index) => {
      const htmlElement = document.createElement("script");
      htmlElement.id = elementId + '-' + index;
      htmlElement.src = value;
      htmlElement.type = "text/javascript";
      document.head.appendChild(htmlElement);
    });
  }

  const removeScriptSourceFromDocumentHead = (elementId: string, srcCount: number) => {
    for (let i = 0; i < srcCount; i++) {
      const element = document.getElementById(elementId + '-' + i);
      if (element) {
        element.parentElement.removeChild(element);
      }
    }

  }

  const uniqueId = epiPropertyValue(props.uniqueId);
  const id = "cs-item-" + uniqueId;
  const scriptElementId = "script-block-" + uniqueId;
  const cssElementId = "css-block-" + uniqueId;
  let gotContent = false;
  let gotPureContent = false;

  const styleValue = epiPropertyValue(props.cssContent);
  const styleValueGlobal = epiPropertyValue(props.cssContentGlobal);
  const scriptValue = epiPropertyValue(props.scriptContent);
  const scriptValueGlobal = epiPropertyValue(props.scriptContentGlobal);
  const pureScriptValue = epiPropertyValue(props.scriptPureContent);
  const fitToContentGrid = epiPropertyValue(props.fitToContentGrid);
  const scriptSources = epiPropertyValue(props.headScripts);
  const htmlContent = epiPropertyValue(props.htmlContent);

  if (styleValue && styleValue !== "") {
    gotContent = true;
  }

  if (styleValueGlobal && styleValueGlobal !== "") {
    gotContent = true;
  }

  if (scriptValue && scriptValue !== "") {
    gotContent = true;
  }

  if (scriptValueGlobal && scriptValueGlobal !== "") {
    gotContent = true;
  }

  if (pureScriptValue && pureScriptValue !== "") {
    gotContent = true;
    gotPureContent = true;
  }

  if (scriptSources && scriptSources.length > 0) {
    gotContent = true;
  }

  if (htmlContent && htmlContent !== "") {
    gotContent = true;
  }

  useEffect(() => {

    if (styleValue && styleValue !== "") {
      addStyleElementToDocument(cssElementId, styleValue);
    }

    if (styleValueGlobal && styleValueGlobal !== "") {
      addStyleElementToDocument(cssElementId + "-global", styleValueGlobal);
    }

    if (scriptSources && scriptSources.length > 0) {
      addScriptSourceToDocumentHead(scriptElementId + '-source', scriptSources);
    }

    if (scriptValue && scriptValue !== "") {
      addScriptElementToDocument(scriptElementId, scriptValue);
    }

    if (scriptValueGlobal && scriptValueGlobal !== "") {
      addScriptElementToDocument(scriptElementId + "-global", scriptValueGlobal);
    }

    return () => {
      removeElementFromDocument(cssElementId);
      removeElementFromDocument(cssElementId + "-global");
      removeElementFromDocument(scriptElementId);
      removeElementFromDocument(scriptElementId + "-global");
      if (scriptSources && scriptSources.length > 0) {
        removeScriptSourceFromDocumentHead(scriptElementId + '-source', scriptSources.length)
      }

    }
  }, []);

  if (!gotContent) {
    return (<></>);
  }

  if (gotPureContent && fitToContentGrid) {
    return (
      <GridWrap className='grid'>
        <div id={id} className={createGridClasses(12, 6, id + " " + createOffsetGridClassLarge(4))}>
          <div dangerouslySetInnerHTML={{ __html: pureScriptValue }} />
        </div>
      </GridWrap>
    );
  }

  if (gotPureContent) {
    return (
      <div id={id} className={id} dangerouslySetInnerHTML={{ __html: pureScriptValue }}></div>
    );
  }

  if (htmlContent) {
    return (
      <div id={id} className={id} dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
    );
  }

  return (
    <div id={id} className={id}></div>
  );
}

export default connect(state => ({
  currentBreakpoint: state.currentBreakpoint,
}))(CssAndScriptBlock);